import type {
  VariableCategory,
  VariableFallbackPolicy,
} from "../components/variable/extension";

export interface VariableDefinition {
  key: string;
  category: VariableCategory;
  label: string;
  description: string;
  default_fallback: VariableFallbackPolicy;
  default_literal: string | null;
}

/**
 * V1 variable catalog (spec §5.6).
 *
 * MUST mirror `backend/app/services/outreach/variable_catalog.py::V1_CATALOG`
 * exactly — `key`, `category`, `default_fallback`, `default_literal` are part
 * of the wire contract resolved when an inline AI prompt is rendered.
 *
 * V1 is hardcoded on both sides (intentional duplication — keeps the
 * insertion menu and the toolbar picker zero-latency offline). The list is
 * small enough that drift is easy to spot in code review.
 *
 * TODO(v2): fetch custom org-scoped variables via TanStack Query so they can
 * be added without a frontend release. Until then, when adding/removing/
 * relabelling a variable, change BOTH this file AND the Python module in the
 * same PR.
 */
export const VARIABLE_CATALOG: readonly VariableDefinition[] = [
  {
    key: "first_name",
    category: "person",
    label: "First Name",
    description: "Candidate's first name (smart-capitalised).",
    default_fallback: "block_enrollment",
    default_literal: null,
  },
  {
    key: "last_name",
    category: "person",
    label: "Last Name",
    description: "Candidate's last name.",
    default_fallback: "block_enrollment",
    default_literal: null,
  },
  {
    key: "full_name",
    category: "person",
    label: "Full Name",
    description: "Candidate's full name.",
    default_fallback: "block_enrollment",
    default_literal: null,
  },
  {
    key: "current_title",
    category: "person",
    label: "Current Title",
    description: "Most recent job title from experience.",
    default_fallback: "block_enrollment",
    default_literal: null,
  },
  {
    key: "current_company",
    category: "person",
    label: "Current Company",
    description: "Most recent company name.",
    default_fallback: "block_enrollment",
    default_literal: null,
  },
  {
    key: "current_company_domain",
    category: "person",
    label: "Current Company Domain",
    description: "Most recent company's domain (for logos / links).",
    default_fallback: "literal_fallback",
    default_literal: "",
  },
  {
    key: "current_education",
    category: "person",
    label: "Current Education",
    description: "Most recent education line: '{degree}, {school}'.",
    default_fallback: "literal_fallback",
    default_literal: "",
  },
  {
    key: "city",
    category: "person",
    label: "City",
    description: "Candidate location city.",
    default_fallback: "literal_fallback",
    default_literal: "",
  },
  {
    key: "country",
    category: "person",
    label: "Country",
    description: "Candidate location country.",
    default_fallback: "literal_fallback",
    default_literal: "",
  },
  {
    key: "linkedin_url",
    category: "person",
    label: "Social URL",
    description: "Candidate Social URL.",
    default_fallback: "literal_fallback",
    default_literal: "",
  },
  {
    key: "sender_first_name",
    category: "sender",
    label: "Sender First Name",
    description: "The sender's first name (from sequence settings).",
    default_fallback: "block_enrollment",
    default_literal: null,
  },
  {
    key: "sender_last_name",
    category: "sender",
    label: "Sender Last Name",
    description: "The sender's last name (from sequence settings).",
    default_fallback: "block_enrollment",
    default_literal: null,
  },
  {
    key: "sender_full_name",
    category: "sender",
    label: "Sender Full Name",
    description: "The sender's full name (from sequence settings).",
    default_fallback: "block_enrollment",
    default_literal: null,
  },
  {
    key: "sender_email",
    category: "sender",
    label: "Sender Email",
    description: "The sender's email address (from sequence settings).",
    default_fallback: "block_enrollment",
    default_literal: null,
  },
  {
    key: "sender_company",
    category: "sender",
    label: "Sender Company",
    description: "The sender's organization name (from sequence settings).",
    default_fallback: "block_enrollment",
    default_literal: null,
  },
  {
    key: "job_title",
    category: "job",
    label: "Job Title",
    description: "Title of the job in scope.",
    default_fallback: "block_enrollment",
    default_literal: null,
  },
  {
    key: "job_company",
    category: "job",
    label: "Job Company",
    description:
      "Employer name for the job in scope (the employer profile attached to the job).",
    default_fallback: "literal_fallback",
    default_literal: "",
  },
] as const;

/** Lookup map keyed by canonical `key`. */
export const VARIABLE_CATALOG_BY_KEY: Readonly<
  Record<string, VariableDefinition>
> = Object.freeze(
  Object.fromEntries(VARIABLE_CATALOG.map((v) => [v.key, v])),
);

const CATEGORY_ORDER: readonly VariableCategory[] = [
  "person",
  "sender",
  "job",
] as const;

export type GroupedVariables = Record<VariableCategory, VariableDefinition[]>;

/**
 * Group the catalog by category, preserving the catalog's insertion order
 * within each group and the canonical category order across groups.
 */
export function groupVariablesByCategory(
  catalog: readonly VariableDefinition[] = VARIABLE_CATALOG,
): GroupedVariables {
  const grouped: GroupedVariables = {
    person: [],
    sender: [],
    job: [],
  };
  for (const entry of catalog) {
    grouped[entry.category].push(entry);
  }
  return grouped;
}

export function findVariableByKey(
  key: string,
): VariableDefinition | undefined {
  return VARIABLE_CATALOG_BY_KEY[key];
}

export const VARIABLE_CATEGORY_ORDER = CATEGORY_ORDER;

export const VARIABLE_CATEGORY_LABELS: Readonly<
  Record<VariableCategory, string>
> = Object.freeze({
  person: "Person",
  sender: "Sender",
  job: "Job",
});
